/**
 * 05 · Exit interception and 06 · discount step.
 *
 * The ladder has four rungs and this file owns the middle two:
 *   1. the paywall
 *   2. ONE interception, showing what is already computed but locked
 *   3. ONE discount step, and only when a live discount backs it
 *   4. leaving is allowed — nothing intercepts again
 *
 * Both sheets keep "leave anyway" at ordinary secondary weight. An interception
 * that hides its own exit is not persuasion, it is a trap, and the person came
 * here to be understood.
 */
import React from 'react';
import { Icon } from '../app/icons';
import { c, hair, f, ACT, scoreColor, patternZone } from '../app/tokens';
import { Col, Meta, Muted, Row } from '../app/primitives';
import { useTt } from '../i18n/useT';
import { PRICE_MONTH, PromoState, money } from '../config/paywall';
import { PaywallProfile } from './data';
import { OK, Serif, Sheet, SoftLink, Cta } from './ui';

/** The eight directions: the taken one scored, the rest honestly closed. */
export const LockList: React.FC<{ profile: PaywallProfile; unlocking?: boolean }> = ({ profile, unlocking }) => {
    const { t } = useTt();

    return (
        <Col gap={6}>
            {profile.directions.map((d) => {
                // One state per row rather than three parallel ternaries —
                // "open" and "soon" are mutually exclusive by construction.
                const open = d.score != null;
                const soon = !!unlocking && !open;
                const { hex } = scoreColor(d.score);
                let tint = 'rgba(232,230,224,0.3)';

                if (open) tint = hex;
                else if (soon) tint = OK;

                return (
                    <Row
                        key={d.code}
                        style={{
                            gap: 11,
                            padding: '8px 11px',
                            borderRadius: 12,
                            border: open || soon ? `1px solid ${tint}${open ? '44' : '28'}` : hair,
                            background: open || soon ? `${tint}${open ? '0f' : '08'}` : c.cardIn,
                        }}
                    >
                        <Icon k={open || soon ? 'check' : 'lock'} size={12} color={tint} />
                        <span
                            style={{
                                fontSize: 12.5,
                                color: open || soon ? c.textPri : c.dim,
                                fontWeight: 400,
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            {d.name}
                        </span>
                        {open ? (
                            <span style={{ fontFamily: f.mono, fontSize: 12.5, color: hex }}>{d.score}</span>
                        ) : (
                            <Meta style={{ fontSize: 9.5, color: soon ? OK : c.muted, letterSpacing: '0.12em' }}>
                                {t(soon ? 'pw.unlocks' : 'pw.locked')}
                            </Meta>
                        )}
                    </Row>
                );
            })}
        </Col>
    );
};

interface ExitProps {
    open: boolean;
    profile: PaywallProfile;
    onStay: () => void;
    onLeave: () => void;
}

/** 05 · one interception, arguing with what is already measured. */
export const ExitIntent: React.FC<ExitProps> = ({ open, profile, onStay, onLeave }) => {
    const { t } = useTt();
    const { blocker } = profile;
    const bz = blocker ? patternZone(blocker.pct, blocker.pol) : null;

    return (
        <Sheet open={open} onDismiss={onStay}>
            <div style={{ padding: '18px 20px 0' }}>
                <Meta style={{ fontSize: 9.5, color: ACT, letterSpacing: '0.16em' }}>{t('pw.exit.eyebrow')}</Meta>
                <Serif size={25} style={{ marginTop: 12, lineHeight: 1.22 }}>
                    {t('pw.exit.title')}
                </Serif>
                <Muted style={{ fontSize: 13, marginTop: 10 }}>{t('pw.exit.sub')}</Muted>
            </div>
            <div style={{ padding: '18px 20px 0' }}>
                <LockList profile={profile} unlocking />
            </div>
            {blocker && bz && (
                <div
                    style={{
                        margin: '16px 20px 0',
                        padding: '13px 15px',
                        border: `1px solid ${bz.hex}30`,
                        background: `${bz.hex}0d`,
                        borderRadius: 14,
                    }}
                >
                    <Row style={{ gap: 10 }}>
                        <Icon k="tPats" size={14} color={bz.hex} style={{ flex: '0 0 auto' }} />
                        <span style={{ fontSize: 12.5, color: c.text, fontWeight: 300, flex: 1 }}>
                            {t('pw.exit.patterns', { name: blocker.name })}
                        </span>
                    </Row>
                </div>
            )}
            <div style={{ padding: '20px 20px 30px' }}>
                <Cta label={t('pw.exit.cta')} onClick={onStay} />
                <SoftLink label={t('pw.exit.leave')} onClick={onLeave} />
            </div>
        </Sheet>
    );
};

/**
 * 06 · the discount step.
 *
 * Rendered only when `promo.valid` — a discounted price the billing side cannot
 * honour is not an offer we are allowed to print, so without one the ladder
 * skips straight to letting the person go.
 */
export const Downsell: React.FC<{
    open: boolean;
    promo: PromoState;
    onAccept: () => void;
    onLeave: () => void;
    onDismiss: () => void;
}> = ({ open, promo, onAccept, onLeave, onDismiss }) => {
    const { t } = useTt();

    return (
        <Sheet open={open} onDismiss={onDismiss}>
            <div style={{ padding: '18px 20px 0' }}>
                <Meta style={{ fontSize: 9.5, color: ACT, letterSpacing: '0.16em' }}>{t('pw.down.eyebrow')}</Meta>
                <Serif size={25} style={{ marginTop: 12, lineHeight: 1.22 }}>
                    {t('pw.down.title')}
                </Serif>
                <Muted style={{ fontSize: 13, marginTop: 10 }}>{t('pw.down.sub', { percent: promo.percent })}</Muted>
            </div>
            <Row style={{ margin: '20px 20px 0', gap: 11, alignItems: 'stretch' }}>
                <Col
                    style={{
                        flex: 1,
                        padding: '13px 14px',
                        border: hair,
                        borderRadius: 15,
                        background: c.cardIn,
                        opacity: 0.55,
                    }}
                    gap={6}
                >
                    <Meta style={{ fontSize: 9, color: c.muted, letterSpacing: '0.12em' }}>{t('pw.down.regular')}</Meta>
                    <span
                        style={{
                            fontFamily: f.mono,
                            fontSize: 19,
                            fontWeight: 300,
                            color: c.muted,
                            letterSpacing: '-0.03em',
                        }}
                    >
                        {money(PRICE_MONTH)}
                    </span>
                    <Meta style={{ fontSize: 9.5, color: c.muted, textTransform: 'none', letterSpacing: 'normal' }}>
                        {t('pw.down.period')}
                    </Meta>
                </Col>
                <Col
                    style={{
                        flex: 1.3,
                        padding: '13px 14px',
                        border: `1px dashed ${ACT}88`,
                        borderRadius: 15,
                        background: `${ACT}0f`,
                    }}
                    gap={6}
                >
                    <Meta style={{ fontSize: 9, color: ACT, letterSpacing: '0.12em' }}>{t('pw.down.discounted')}</Meta>
                    <Row style={{ gap: 8, alignItems: 'baseline' }}>
                        <span
                            style={{
                                fontFamily: f.mono,
                                fontSize: 13,
                                color: c.subtle,
                                textDecoration: 'line-through',
                            }}
                        >
                            {money(PRICE_MONTH)}
                        </span>
                        <span
                            style={{
                                fontFamily: f.mono,
                                fontSize: 20,
                                fontWeight: 400,
                                color: ACT,
                                letterSpacing: '-0.03em',
                            }}
                        >
                            {money(promo.price)}
                        </span>
                    </Row>
                    <Meta style={{ fontSize: 9.5, color: c.muted, textTransform: 'none', letterSpacing: 'normal' }}>
                        {t('pw.down.period')}
                    </Meta>
                </Col>
            </Row>
            <div style={{ padding: '20px 20px 30px' }}>
                <Cta label={t('pw.down.cta')} onClick={onAccept} />
                <SoftLink label={t('pw.exit.leave')} onClick={onLeave} />
            </div>
        </Sheet>
    );
};
